import { cx } from '../../../utils/cx';
import { isDatePastOrEqual } from '@goodneighbor/utilities/date/index';

export type EventStatus =
  | 'pending'
  | 'private'
  | 'open'
  | 'full'
  | 'inProgress'
  | 'complete';

export interface EventStatusBadgeProps {
  status: EventStatus;
  className?: string;
}

const statusConfig: Record<
  EventStatus,
  { label: string; bgColor: string; textColor: string; borderColor: string }
> = {
  pending: {
    label: 'Pending Approval',
    bgColor: 'bg-utility-warning-50',
    textColor: 'text-utility-warning-700',
    borderColor: 'border-utility-warning-200'
  },
  private: {
    label: 'Private',
    bgColor: 'bg-utility-orange-50',
    textColor: 'text-utility-orange-700',
    borderColor: 'border-utility-orange-200'
  },
  open: {
    label: 'Open',
    bgColor: 'bg-utility-success-50',
    textColor: 'text-utility-success-700',
    borderColor: 'border-utility-success-200'
  },
  full: {
    label: 'Full',
    bgColor: 'bg-utility-blue-50',
    textColor: 'text-utility-blue-700',
    borderColor: 'border-utility-blue-200'
  },
  inProgress: {
    label: 'In Progress',
    bgColor: 'bg-utility-purple-50',
    textColor: 'text-utility-purple-700',
    borderColor: 'border-utility-purple-200'
  },
  complete: {
    label: 'Complete',
    bgColor: 'bg-utility-gray-50',
    textColor: 'text-utility-gray-700',
    borderColor: 'border-utility-gray-200'
  }
};

/**
 * Calculates the event status based on approval state and capacity
 * @param isApproved - null = pending, true = approved, false = private
 * @param overallCapacity - total event capacity
 * @param spotsTaken - number of spots currently filled
 * @returns EventStatus
 */
export function getEventStatus(
  isApproved: boolean | null | undefined,
  overallCapacity?: number | null,
  spotsTaken?: number | null,
  startsAt?: string | null
): EventStatus {
  if (isApproved === null || isApproved === undefined) {
    return 'pending';
  }

  if (isApproved === false) {
    return 'private';
  }

  if (isApproved === true) {
    if (startsAt && isDatePastOrEqual(new Date(startsAt))) {
      return 'complete';
    }

    if (
      typeof overallCapacity === 'number' &&
      typeof spotsTaken === 'number' &&
      overallCapacity > 0
    ) {
      if (spotsTaken >= overallCapacity) {
        return 'full';
      }
    }
    return 'open';
  }

  return 'pending';
}

export function EventStatusBadge({ status, className }: EventStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        className
      )}
    >
      {config.label}
    </span>
  );
}
